import { useMemo } from 'react';
import { DropdownItemProps } from 'semantic-ui-react';
import orderBy from 'lodash/orderBy';
import deburr from 'lodash/deburr';
import { Initiator } from '../types';
import { InitiatorsApi } from '../api';
import { ApiError, defaultErrorHandler } from 'api';
import { useQuery } from 'react-query';

export interface InitiatorLists {
  initiators: Initiator[];
  dropdownItems: DropdownItemProps[];
  indexedInitiators: Record<number, Initiator>;
  isFetched: boolean;
  isFetching: boolean;
}

export const useFetchInitiators = (options?: {
  staleTime?: number;
}): InitiatorLists => {
  const {
    data: initiators,
    isFetching,
    isFetched,
  } = useQuery('initiators', InitiatorsApi.getAll, {
    staleTime: 15 * 60 * 1000,
    onSettled: (_, error) =>
      error && error instanceof ApiError && defaultErrorHandler(error),
    ...options,
  });

  const indexedInitiators = useMemo(
    () =>
      (initiators || []).reduce((result, item) => {
        result[item.id] = item;
        return result;
      }, {} as Record<number, Initiator>),
    [initiators]
  );

  const dropdownItems = useMemo(() => {
    const activeInitiators = (initiators || []).filter(
      (initiators) => initiators.isActive
    );

    const items = activeInitiators.map((initiator) => ({
      key: initiator.id,
      value: initiator.id,
      text: initiator.name,
    }));

    const sortedItems = orderBy(items, (item) =>
      deburr(item.text).toLowerCase()
    );

    return sortedItems;
  }, [initiators]);

  return {
    initiators: initiators || [],
    dropdownItems,
    indexedInitiators,
    isFetched,
    isFetching,
  };
};
